import React from "react";
import { Box, Button, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Plus, X } from "lucide-react";
import { useHd } from "../HelpdeskContext";
import { T, TYPE } from "../../user/complaints/shared";

// Condition DSL editor: { all: [{field, op, value}], any: [...] }.
// Field value inputs adapt to the field (status list, priority list, ...).

export const useFields = () => {
  const ctx = useHd();
  const opt = (list, label = (x) => x.name, value = (x) => x._id) => list.map((x) => [String(value(x)), label(x)]);
  return [
    ["channel", "Channel", [["email", "Email"], ["chat", "Chat"], ["whatsapp", "WhatsApp"], ["voice", "Voice"], ["web", "Web"], ["api", "API"]]],
    ["statusId", "Status", opt(ctx.statuses)],
    ["statusType", "Status type", [["new", "New"], ["open", "Open"], ["pending", "Pending"], ["on_hold", "On hold"], ["resolved", "Resolved"], ["closed", "Closed"]]],
    ["priorityId", "Priority", opt(ctx.priorities)],
    ["priorityLevel", "Priority level (number)", null],
    ["categoryId", "Category", opt(ctx.categories.filter((c) => c.level === "category"))],
    ["subcategoryId", "Subcategory", opt(ctx.categories.filter((c) => c.level === "subcategory"))],
    ["teamId", "Team", opt(ctx.teams)],
    ["assignee.id", "Agent", opt(ctx.agents, (a) => a.name, (a) => a.memberId)],
    ["isAssigned", "Is assigned", [["true", "Yes"], ["false", "No"]]],
    ["tags", "Tags", null],
    ["subject", "Subject", null],
    ["description", "Description", null],
    ["requester.email", "Customer email", null],
    ["requester.emailDomain", "Customer email domain", null],
    ["handledBy", "Handled by", [["none", "Nobody yet"], ["ai", "AI"], ["human", "Human"]]],
    ["escalated", "Escalated", [["true", "Yes"], ["false", "No"]]],
    ["ai.intent", "AI intent", null],
    ["ai.sentiment", "AI sentiment", [["positive", "Positive"], ["neutral", "Neutral"], ["negative", "Negative"]]],
    ["message.content", "Message text", null],
    ["hoursSinceCreated", "Hours since created", null],
    ["hoursSinceUpdated", "Hours since updated", null],
    ["hoursSinceCustomerReply", "Hours since customer reply", null],
    ["hoursSinceAgentReply", "Hours since agent reply", null],
    ["hoursSinceResolved", "Hours since resolved", null],
    ...ctx.customFields.filter((f) => f.entity === "ticket").map((f) => [`customFields.${f.key}`, `Field: ${f.label}`, ["dropdown", "radio"].includes(f.type) ? f.options.map((o) => [o, o]) : null]),
  ];
};

const OPS = [
  ["eq", "is"], ["neq", "is not"], ["in", "is one of"], ["contains", "contains"], ["not_contains", "does not contain"], ["starts_with", "starts with"],
  ["gt", ">"], ["gte", "≥"], ["lt", "<"], ["lte", "≤"], ["exists", "is set"], ["not_exists", "is empty"],
  ["changed", "changed"], ["changed_to", "changed to"], ["changed_from", "changed from"],
];

const Row = ({ c, onChange, onRemove, fields }) => {
  const f = fields.find((x) => x[0] === c.field);
  const needsValue = !["exists", "not_exists", "changed"].includes(c.op);
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <TextField select size="small" value={c.field} onChange={(e) => onChange({ ...c, field: e.target.value, value: "" })} sx={{ minWidth: 200 }}>
        {fields.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
      </TextField>
      <TextField select size="small" value={c.op} onChange={(e) => onChange({ ...c, op: e.target.value })} sx={{ minWidth: 140 }}>
        {OPS.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
      </TextField>
      {needsValue && (f?.[2] ? (
        <TextField select size="small" value={c.op === "in" ? [].concat(c.value || []) : c.value ?? ""} SelectProps={{ multiple: c.op === "in" }}
          onChange={(e) => onChange({ ...c, value: e.target.value })} sx={{ minWidth: 180 }}>
          {f[2].map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
        </TextField>
      ) : (
        <TextField size="small" value={c.value ?? ""} onChange={(e) => onChange({ ...c, value: e.target.value })} sx={{ minWidth: 180 }} />
      ))}
      <IconButton size="small" onClick={onRemove}><X size={15} /></IconButton>
    </Stack>
  );
};

export default function ConditionBuilder({ value, onChange }) {
  const fields = useFields();
  const v = { all: value?.all || [], any: value?.any || [] };
  const setGroup = (g, list) => onChange({ ...v, [g]: list });
  // plain render function (not a component) so inputs keep focus while typing
  const group = (g, label) => (
    <Box key={g} sx={{ p: 1.5, border: `1px solid ${T.border}`, borderRadius: "10px", mb: 1.5 }}>
      <Typography sx={{ fontSize: TYPE.micro, fontWeight: 700, color: T.inkSecondary, mb: 1 }}>{label}</Typography>
      {v[g].map((c, i) => (
        <Row key={i} c={c} fields={fields} onChange={(nc) => setGroup(g, v[g].map((x, j) => (j === i ? nc : x)))} onRemove={() => setGroup(g, v[g].filter((_, j) => j !== i))} />
      ))}
      <Button size="small" startIcon={<Plus size={14} />} onClick={() => setGroup(g, [...v[g], { field: "channel", op: "eq", value: "" }])}>Add condition</Button>
    </Box>
  );
  return (
    <Box>
      {group("all", "ALL of these conditions (AND)")}
      {group("any", "…and ANY of these (OR) — optional")}
    </Box>
  );
}
